import React, { useEffect, useState, useRef } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import OmFirmaet from './OmFirmaet';
import BeforeAfterSlider from '../components/BeforeAfterSlider';
import { beforeAfterMock } from '../data';
import { CheckCircle2, ArrowRight, X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { injectInternalLinks, stripAllLinks } from '../lib/linkInjector';

export default function ServicePage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [allBeforeAfter, setAllBeforeAfter] = useState([]);
  const [loadingBeforeAfter, setLoadingBeforeAfter] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Smart Price Calculator State
  const [calcService, setCalcService] = useState('indendoers');
  const [calcArea, setCalcArea] = useState(50);
  const [calcQty, setCalcQty] = useState(3);

  const getMinPrice = () => {
    switch (calcService) {
      case 'indendoers':
        return calcArea * 85;
      case 'spartling':
        return calcArea * 110;
      case 'doere':
        return calcQty * 1200;
      case 'vinduer':
        return calcQty * 1500;
      case 'facade':
        return calcArea * 190;
      default:
        return 0;
    }
  };

  const getMaxPrice = () => {
    switch (calcService) {
      case 'indendoers':
        return calcArea * 125;
      case 'spartling':
        return calcArea * 160;
      case 'doere':
        return calcQty * 1800;
      case 'vinduer':
        return calcQty * 2200;
      case 'facade':
        return calcArea * 290;
      default:
        return 0;
    }
  };

  const formatPrice = (num) => {
    return new Intl.NumberFormat('da-DK').format(Math.round(num));
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(data => {
        setPage(data);
        setLoading(false);
      })
      .catch(() => {
        setPage(null);
        setLoading(false);
      });
  }, [slug]);

  // Fetch gallery matching this service
  useEffect(() => {
    if (!page) return;

    const isLocalLandingPage = slug && slug.startsWith('maler-i-');
    if (isLocalLandingPage) {
      const defaultGalleryImages = [
        "https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-5506.jpeg",
        "https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-5502.jpeg",
        "https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-5499.jpeg",
        "https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-5500.jpeg"
      ];
      setGallery({
        title: { da: "Vores malerarbejde" },
        images: defaultGalleryImages,
        service_slug: slug
      });
      return;
    }

    fetch('https://api.dev.auxo.dk/public/content/galleries')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch galleries');
        return res.json();
      })
      .then(data => {
        // Find gallery that matches the service slug or title
        const found = data.find(g => {
          const serviceSlugMatch = g.service_slug === slug;
          const titleMatch = g.title?.da?.toLowerCase() === page.title?.da?.toLowerCase();
          const partialTitleMatch = page.title?.da?.toLowerCase().includes(g.title?.da?.toLowerCase());
          const slugPartialMatch = slug.toLowerCase().includes(g.title?.da?.toLowerCase().replace(/\s+/g, '-'));
          
          return serviceSlugMatch || titleMatch || partialTitleMatch || slugPartialMatch;
        });
        setGallery(found);
      })
      .catch(err => {
        console.error('Error fetching gallery:', err);
      });
  }, [slug, page]);

  // Fetch before/after items
  useEffect(() => {
    setLoadingBeforeAfter(true);
    fetch('https://api.dev.auxo.dk/public/content/before_after')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0));
          setAllBeforeAfter(sorted);
        }
        setLoadingBeforeAfter(false);
      })
      .catch(err => {
        console.error('Fejl ved hentning af før/efter billeder:', err);
        setLoadingBeforeAfter(false);
      });
  }, []);

  const getMatchingBeforeAfter = () => {
    if (!allBeforeAfter || allBeforeAfter.length === 0) return [];
    const textToMatch = `${slug} ${page?.title?.da || ''}`.toLowerCase();

    let matched = [];
    if (textToMatch.includes('spartl')) {
      matched = allBeforeAfter.filter(item => (item.title?.da || '').toLowerCase().includes('spartl'));
    } else if (textToMatch.includes('filt')) {
      matched = allBeforeAfter.filter(item => {
        const t = (item.title?.da || '').toLowerCase();
        return t.includes('filt') || t.includes('spartl');
      });
    } else if (textToMatch.includes('loft')) {
      matched = allBeforeAfter.filter(item => (item.title?.da || '').toLowerCase().includes('loft'));
    } else if (textToMatch.includes('facade') || textToMatch.includes('gavl')) {
      matched = allBeforeAfter.filter(item => {
        const t = (item.title?.da || '').toLowerCase();
        return t.includes('facade') || t.includes('gavl');
      });
    } else if (textToMatch.includes('vindue') || textToMatch.includes('doer') || textToMatch.includes('dør') || textToMatch.includes('traevaerk') || textToMatch.includes('træværk')) {
      matched = allBeforeAfter.filter(item => {
        const t = (item.title?.da || '').toLowerCase();
        return t.includes('døre') || t.includes('vinduer') || t.includes('træværk');
      });
    } else if (textToMatch.includes('vaeg') || textToMatch.includes('væg')) {
      matched = allBeforeAfter.filter(item => {
        const t = (item.title?.da || '').toLowerCase();
        return t.includes('væg') || t.includes('vægge') || t.includes('farvemaling');
      });
    } else if (textToMatch.includes('tapet')) {
      matched = allBeforeAfter.filter(item => {
        const t = (item.title?.da || '').toLowerCase();
        return t.includes('tapet') || t.includes('væg') || t.includes('spartl');
      });
    } else if (slug && slug.startsWith('maler-i-')) {
      matched = allBeforeAfter;
    }

    if (matched.length > 0) {
      return matched.slice(0, 3);
    }
    return [];
  };

  const currentBeforeAfterItems = getMatchingBeforeAfter();

  // Keyboard navigation for Lightbox
  useEffect(() => {
    if (activeImageIndex === null || !gallery) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setActiveImageIndex(null);
      } else if (e.key === 'ArrowRight') {
        setActiveImageIndex((prev) => (prev + 1) % gallery.images.length);
      } else if (e.key === 'ArrowLeft') {
        setActiveImageIndex((prev) => (prev - 1 + gallery.images.length) % gallery.images.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeImageIndex, gallery]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!page) {
    return <Navigate to="/" replace />;
  }

  if (page.template === 'about') {
    return <OmFirmaet page={page} />;
  }

  const getGridClasses = (index) => {
    switch (index) {
      case 0:
        return "lg:col-span-2 lg:row-span-2 h-[280px] sm:h-[400px] lg:h-[624px]";
      case 1:
        return "h-[280px] lg:h-[300px]";
      case 2:
        return "h-[280px] lg:h-[300px]";
      case 3:
        return "h-[280px] lg:h-[300px]";
      case 4:
        return "h-[280px] lg:h-[300px]";
      case 5:
        return "h-[280px] lg:h-[300px]";
      default:
        return "h-[280px]";
    }
  };

  const seoTitle = slug === 'tapetsering'
    ? "Tapetsering i Sønderborg og østlige Sønderjylland | Få en pris her"
    : slug === 'maling-af-facade'
    ? "Maling af facade i Sønderborg og østlige Sønderjylland | Få tilbud"
    : slug === 'maler-i-aabenraa'
    ? "Maler i Aabenraa | Malerfirma til privat og erhverv | Få tilbud"
    : slug === 'maler-i-augustenborg'
    ? "Maler i Augustenborg | Malerfirma til privat og erhverv | Få tilbud"
    : slug === 'maler-i-guderup'
    ? "Maler i Guderup | Malerfirma til privat og erhverv | Få tilbud"
    : slug === 'maler-i-dybboel'
    ? "Maler i Dybbøl | Malerfirma til privat og erhverv | Få et tilbud"
    : slug === 'maler-i-nordborg'
    ? "Maler i Nordborg | Malerfirma til privat og erhverv | Få tilbud"
    : slug === 'maler-i-padborg'
    ? "Maler i Padborg | Malerfirma til privat og erhverv | Få tilbud"
    : page.title?.da;

  const seoDescription = slug === 'tapetsering'
    ? "Tapetsering i Sønderborg, Gråsten og på Als. Få hjælp til tapetsering af væg, klargøring og opsætning med flot finish. Ring i dag og få en pris."
    : slug === 'maling-af-facade'
    ? "Maling af facade i Sønderborg, på Als og i det østlige Sønderjylland. Få hjælp til facademaling, klargøring og en holdbar løsning. Kontakt os i dag."
    : page.description?.da;

  const getBadgeText = () => {
    if (!page || !page.title?.da) return "Professionelt malerarbejde";
    const title = page.title.da;
    if (slug === 'maler-til-boligforening-ved-fraflytning-og-istandsaettelse') {
      return "+25 malere til boligforening";
    }
    if (slug && slug.startsWith('maler-i-')) {
      const city = title.replace(/Maler i\s+/i, '').trim();
      return `Din lokale maler i ${city}`;
    }
    return `Professionel ${title.toLowerCase()}`;
  };

  const getHeroImageClass = () => {
    const isCity = slug && slug.startsWith('maler-i-');
    const heroSrc = isCity
      ? "https://assets.auxodev.dk/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-6842-2.jpeg"
      : (page.hero_image && page.hero_image[0])
        ? page.hero_image[0]
        : "";

    if (heroSrc.includes('img-6842') || isCity) {
      // Show Mette Frank in full and the van cleanly without cutting off at the neck or legs
      return 'object-[center_24%] sm:object-[center_22%]';
    }
    if (heroSrc.includes('img-7167') || slug === 'opsaetning-af-filt') {
      // Zoom out framing by focusing on the upper action area where the felt & painting happens
      return 'object-[center_16%] sm:object-[center_18%]';
    }
    if (heroSrc.includes('img-7173') || slug === 'maling-af-traevaerk' || slug === 'maling-af-doere') {
      return 'object-[46%_54%] sm:object-[46%_54%] contrast-[1.14] brightness-[1.03] saturate-[1.06] [image-rendering:-webkit-optimize-contrast]';
    }
    if (heroSrc.includes('img-5310') || heroSrc.includes('img-1547') || slug === 'spartling') {
      return 'object-[center_35%]';
    }
    if (heroSrc.includes('img-5506') || heroSrc.includes('img-5499') || slug === 'tapetsering') {
      return 'object-[center_55%]';
    }
    if (heroSrc.includes('img-5165') || slug === 'maling-af-facade') {
      return 'object-[center_35%]';
    }
    return 'object-center';
  };

  return (
    <>
      <SEO 
        title={seoTitle}
        description={seoDescription}
      />

      {slug === 'tapetsering' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Hvad indebærer tapetsering af væg, og hvornår er det en god løsning?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Tapetsering af væg er en god løsning, når et rum skal føles nyt uden en større renovering. Hos Syddansk hjælper vi med tapetsering i både private hjem og erhverv, hvor tapet kan give vægge mere varme, karakter, ro, dybde og struktur. Målet er glatte flader, præcise samlinger og et udtryk, der passer til rummet."
                }
              },
              {
                "@type": "Question",
                "name": "Hvorfor giver professionel tapetsering af vægge et pænere resultat?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Professionel tapetsering af vægge giver et mere sikkert og gennemført resultat, fordi arbejdet kræver ro, præcision og korrekt forarbejde. Hos Syddansk har vi fokus på mønsterpasning, lodrette baner, korrekt klæber og pæne hjørner, så tapetet fremhæver rummet i stedet for at afsløre fejl."
                }
              },
              {
                "@type": "Question",
                "name": "Hvilket forarbejde er vigtigt før tapetsering?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Før tapetsering vurderer vi altid underlaget, fordi en ren, tør og jævn væg er afgørende for, at tapetet hæfter korrekt og ikke viser ujævnheder efter opsætning. Forarbejdet kan omfatte afrensning, slibning, mindre reparationer og vurdering af gamle skader. Har væggen huller, revner eller ujævnheder, kan vi også hjælpe med spartling af vægge som en del af klargøringen."
                }
              },
              {
                "@type": "Question",
                "name": "Hvad påvirker tapetsering pris og tapetsering priser?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Når du søger efter tapetsering pris eller tapetsering priser, afhænger prisen sjældent af ét standardtal. Hos Syddansk vurderer vi blandt andet væggens stand, rummets størrelse, tapettypen, behov for spartling, slibning eller afrensning, om gammelt tapet skal fjernes, og hvor meget afdækning opgaven kræver. På den måde får du en konkret pris på tapetsering, der tager højde for materialer, tidsforbrug og det ønskede finishniveau."
                }
              },
              {
                "@type": "Question",
                "name": "Kan tapetsering kombineres med andre maleropgaver?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, tapetsering kan med fordel kombineres med andre opgaver, hvis rummet skal have en samlet opfriskning. Hos Syddansk kan vi kombinere tapetarbejdet med maling af vægge eller maling af loft, så finishen bliver ensartet hele vejen rundt."
                }
              }
            ]
          })}
        </script>
      )}

      {slug === 'maling-af-facade' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Hvad er vigtigt at få vurderet før maling af facade og facademaling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Før maling af facade ser Syddansk Malerfirma på underlag, stand og vejrbelastning, fordi facademaling ikke kun handler om farve, men også om beskyttelse. Vi vurderer, om facaden kræver afrensning, reparation af småskader, grunder eller en særlig malingstype, så resultatet holder flot over tid."
                }
              },
              {
                "@type": "Question",
                "name": "Hvordan udføres maling af pudset facade korrekt?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ved maling af pudset facade sørger Syddansk Malerfirma for grundigt forarbejde, fordi revner, løse partier og snavs ellers kan ses gennem den færdige overflade. Vi klargør facaden korrekt, så malingen får gode betingelser for at hæfte, og huset får et roligt og ensartet udtryk. Du kan også læse mere om vores spartling af væg for et helt jævnt resultat."
                }
              },
              {
                "@type": "Question",
                "name": "Hvilken løsning er bedst til facademaling af mursten og murmaling?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Til facademaling af mursten og andre mineralske facader vælger Syddansk Malerfirma en løsning, der passer til murens behov. Den rigtige murmaling skal både give en flot finish og tage hensyn til fugt i konstruktionen, så facaden bliver bedre beskyttet."
                }
              },
              {
                "@type": "Question",
                "name": "Hvad påvirker maling af facade pris og facademaling priser?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Maling af facade pris og facademaling priser afhænger typisk af facadens størrelse, højde, adgangsforhold, underlagets stand og behovet for afrensning eller mindre reparationer. Syddansk Malerfirma vurderer også antal lag maling, valg af facademaling og eventuelt stilladsbehov, før vi giver et konkret tilbud."
                }
              },
              {
                "@type": "Question",
                "name": "Kan en maler hjælpe med maling af hus og facademaling for erhverv?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Ja, Syddansk Malerfirma hjælper med maling af hus for private, boligforeninger og facademaling for erhverv. Ved erhvervsbygninger tilpasser vi planlægningen, så adgang, drift og kunder påvirkes mindst muligt, uden at gå på kompromis med finishen. Du kan også læse om facademaling for erhverv for kontorbygninger og butikker."
                }
              }
            ]
          })}
        </script>
      )}

      {/* Hero Banner */}
      {(Boolean(page.video_file && page.video_file[0]) || Boolean(page.hero_image && page.hero_image[0]) || (slug && slug.startsWith('maler-i-'))) && (
        <div className="relative h-[360px] sm:h-[480px] lg:h-[560px] w-full overflow-hidden bg-slate-950 flex items-center justify-center">
          {page.video_file && page.video_file[0] ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                loop
                muted={isMuted}
                playsInline
                controls
                className="w-full h-full object-contain"
              >
                <source src={page.video_file[0]} type="video/mp4" />
                <source src={page.video_file[0]} type="video/quicktime" />
              </video>
              <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 z-10 flex items-center gap-3 opacity-90 hover:opacity-100 transition-opacity pointer-events-auto">
                <button
                  type="button"
                  onClick={togglePlay}
                  aria-label={isPlaying ? "Pause video" : "Afspil video"}
                  className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border-2 border-white flex items-center justify-center text-white bg-black/60 hover:bg-black/80 backdrop-blur-md cursor-pointer shadow-lg transition-all transform hover:scale-105 active:scale-95"
                >
                  <i className={`fa-solid ${isPlaying ? 'fa-pause' : 'fa-play'} text-sm`} />
                </button>
                <button
                  type="button"
                  onClick={toggleMute}
                  aria-label={isMuted ? "Slå lyd til" : "Slå lyd fra"}
                  className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border-2 border-white flex items-center justify-center text-white bg-black/60 hover:bg-black/80 backdrop-blur-md cursor-pointer shadow-lg transition-all transform hover:scale-105 active:scale-95"
                >
                  <i className={`fa-solid ${isMuted ? 'fa-volume-xmark' : 'fa-volume-high'} text-sm`} />
                </button>
              </div>
            </>
          ) : (
            <>
              <img 
                src={(slug && slug.startsWith('maler-i-')) 
                  ? "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=90,format=auto/assets/656b4e51-500c-4bf5-9ac7-27acb4e8635e/img-6842-2.jpeg" 
                  : (page.hero_image && page.hero_image[0]) 
                    ? (page.hero_image[0].includes('cdn-cgi') 
                        ? page.hero_image[0] 
                        : page.hero_image[0].replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=95,format=auto/assets/')) 
                    : ""} 
                alt={page.title?.da} 
                className={`w-full h-full object-cover ${getHeroImageClass()} opacity-95 transition-all duration-300`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent pointer-events-none" />
            </>
          )}
        </div>
      )}

      {/* Main Content Section */}
      <section className="pt-16 pb-20 bg-white relative">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-8 lg:px-12 relative z-10">

          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              {/* Page Header */}
              <div className="mb-8 flex flex-col items-start gap-3">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-800 tracking-tight leading-[1.15] mb-2">
                  {page.title?.da}
                </h1>
                {page.subtitle?.da && (
                  <p className="text-base md:text-xl font-semibold text-[#0b4e47] leading-relaxed">
                    {page.subtitle.da}
                  </p>
                )}
              </div>

              <div 
                className="prose md:prose-lg prose-slate max-w-none service-content"
                dangerouslySetInnerHTML={{ 
                  __html: injectInternalLinks(
                    (page.content?.da || '').replace(
                      /<a([^>]*href=["']tel:24995995["'][^>]*)>(.*?)<\/a>/gi,
                      (match, attrs, innerText) => {
                        if (innerText.includes('fa-phone')) return match;
                        const cleanText = innerText.replace(/<[^>]*>/g, '').trim();
                        const displayText = cleanText.startsWith('Ring') ? cleanText : `Ring: ${cleanText}`;
                        return `<a${attrs}><i class="fa-solid fa-phone text-xs mr-2" aria-hidden="true"></i><span>${displayText}</span></a>`;
                      }
                    ), 
                    slug
                  ) 
                }}
              />
              <style dangerouslySetInnerHTML={{__html: `
                .service-content ul {
                  list-style-type: none !important;
                  padding-left: 0 !important;
                }
                .service-content ul > li {
                  position: relative !important;
                  padding-left: 1.75rem !important;
                }
                .service-content ul > li::before {
                  content: "" !important;
                  position: absolute !important;
                  left: 0 !important;
                  top: 0.35em !important;
                  width: 1.1rem !important;
                  height: 1.1rem !important;
                  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ff6a00' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/svg%3E") !important;
                  background-size: contain !important;
                  background-repeat: no-repeat !important;
                  background-color: transparent !important;
                  border-radius: 0 !important;
                }
                /* Ensure all CTA buttons in the content are uniformly styled with brand orange */
                .service-content a.btn-orange,
                .service-content a.btn-green,
                .service-content a.btn-outline-orange,
                .service-content a.inline-flex {
                  background-color: #ee6425 !important;
                  color: #ffffff !important;
                  border: none !important;
                  border-radius: 14px !important;
                  box-shadow: 0 4px 12px rgba(238, 100, 37, 0.2) !important;
                  transition: all 0.2s ease-in-out !important;
                  text-decoration: none !important;
                  font-weight: 700 !important;
                  display: inline-flex !important;
                  align-items: center !important;
                  justify-content: center !important;
                  gap: 0.5rem !important;
                }
                .service-content a.btn-orange:hover,
                .service-content a.btn-green:hover,
                .service-content a.btn-outline-orange:hover,
                .service-content a.inline-flex:hover {
                  background-color: #d95318 !important;
                  color: #ffffff !important;
                  box-shadow: 0 6px 16px rgba(238, 100, 37, 0.3) !important;
                  transform: translateY(-1px) !important;
                }
                /* Ensure all regular links in the content are styled with the brand orange color */
                .service-content a:not(.inline-flex):not(.btn-orange):not(.btn-outline-orange):not(.btn-green) {
                  color: #ff6a00 !important;
                  font-weight: 700 !important;
                  text-decoration: underline !important;
                }
                .service-content a:not(.inline-flex):not(.btn-orange):not(.btn-outline-orange):not(.btn-green):hover {
                  color: #e05d00 !important;
                }
                /* Ensure all images in the content have a consistent, balanced size and aspect ratio */
                .service-content img {
                  width: 100% !important;
                  height: 400px !important;
                  object-fit: cover !important;
                }
                @media (max-width: 768px) {
                  .service-content img {
                    height: 280px !important;
                  }
                }
              `}} />

              {/* Før/efter billeder af det som afsnittet handler om (hvis der er nogen der passer ind) */}
              {currentBeforeAfterItems.length > 0 && (
                <div className="mt-12 pt-8 border-t border-slate-200/80">
                  <div className="mb-6">
                    <span className="text-orange-500 font-bold tracking-wider uppercase text-xs mb-1 block">Før / Efter</span>
                    <h2 className="text-2xl font-bold text-slate-800">Se før og efter resultater</h2>
                    <p className="text-stone-600 text-sm mt-1">Træk i skyderen for at sammenligne før og efter vores malerarbejde.</p>
                  </div>
                  <div className={`grid ${currentBeforeAfterItems.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'} gap-6`}>
                    {currentBeforeAfterItems.map((item) => (
                      <div key={item.id} className="w-full">
                        <BeforeAfterSlider 
                          beforeImage={item.before_image?.[0]} 
                          afterImage={item.after_image?.[0]} 
                          title={item.title?.da}
                          position={item.title?.da?.toLowerCase().includes('loft') ? 'top' : 'center'}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {page.faqs?.da && page.faqs.da.trim() !== "" && (
                <div className="mt-16">
                  <h2 className="text-3xl font-bold text-slate-800 mb-8">Ofte stillede spørgsmål:</h2>
                  <div 
                    className="space-y-6 faq-content"
                    dangerouslySetInnerHTML={{ __html: stripAllLinks(page.faqs.da) }}
                  />
                  <style dangerouslySetInnerHTML={{__html: `
                    .faq-content h3 {
                      background-color: #0b4e47;
                      color: white;
                      padding: 1.5rem 1.5rem 0 1.5rem;
                      border-radius: 1rem 1rem 0 0;
                      border: 1px solid rgba(11, 78, 71, 0.8);
                      border-bottom: none;
                      margin-bottom: 0;
                      font-size: 1.25rem;
                      font-weight: 700;
                    }
                    .faq-content p {
                      background-color: #0b4e47;
                      color: rgba(236, 253, 245, 0.8);
                      padding: 0 1.5rem 1.5rem 1.5rem;
                      border-radius: 0 0 1rem 1rem;
                      border: 1px solid rgba(11, 78, 71, 0.8);
                      border-top: none;
                      margin-top: 0;
                      margin-bottom: 1.5rem;
                      line-height: 1.625;
                    }
                    .faq-content p a {
                      color: #ff6a00 !important;
                      font-weight: 700 !important;
                      text-decoration: underline !important;
                    }
                    .faq-content p a:hover {
                      color: #e05d00 !important;
                    }
                  `}} />
                </div>
              )}



            </div>

            <div className="lg:col-span-5 lg:sticky lg:top-32">
              {page.benefits?.da && page.benefits.da.trim() !== "" && (() => {
                const benefitsCount = (page.benefits.da.match(/<li[\s>]/gi) || []).length || 4;
                return (
                  <div className="bg-[#0b4e47] text-white rounded-[2rem] p-8 md:p-10 shadow-xl border border-[#0b4e47]/20 relative overflow-hidden">
                    {/* Subtle decorative background sketch in brand lime */}
                    <svg className="absolute -right-10 -bottom-10 w-48 h-48 text-[#79b43b] opacity-10 pointer-events-none" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M10,80 Q40,120 80,80 T150,80" strokeLinecap="round" />
                      <path d="M20,90 Q50,130 90,90 T160,90" strokeLinecap="round" />
                    </svg>

                    <h2 className="text-2xl font-bold mb-6 text-white tracking-tight">{benefitsCount} fordele når du vælger os</h2>
                    <div 
                      className="space-y-4 benefits-content"
                      dangerouslySetInnerHTML={{ __html: stripAllLinks(page.benefits.da) }}
                    />
                    <style dangerouslySetInnerHTML={{__html: `
                      .benefits-content ul {
                        list-style: none;
                        padding: 0;
                        margin: 0;
                      }
                      .benefits-content li {
                        display: flex;
                        align-items: flex-start;
                        gap: 0.75rem;
                        margin-bottom: 1.25rem;
                        font-size: 0.875rem;
                        font-weight: 500;
                        color: rgba(236, 253, 245, 0.9);
                        line-height: 1.5;
                      }
                      .benefits-content li::before {
                        content: "";
                        display: inline-block;
                        width: 1.25rem;
                        height: 1.25rem;
                        flex-shrink: 0;
                        margin-top: 0.125rem;
                        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Ccircle cx='12' cy='12' r='10' stroke='%23ff6a00' stroke-width='2' fill='none'/%3E%3Ccircle cx='12' cy='12' r='7' fill='%23ff6a00'/%3E%3Cpath d='M9 12l2 2 4-4' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
                        background-size: contain;
                        background-repeat: no-repeat;
                      }
                    `}} />
                    <div className="mt-10 pt-8 border-t border-white/10 relative z-10">
                      <Link 
                        to="/kontakt" 
                        className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 bg-[#ff6a00] hover:bg-[#e05d00] text-white font-extrabold text-sm rounded-[14px] transition-all duration-200 transform hover:-translate-y-0.5 active:scale-98 shadow-md shadow-black/20 uppercase tracking-wider w-full sm:w-auto cursor-pointer"
                      >
                        <span>Kontakt os nu</span>
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                );
              })()}

            </div>
          </div>
        </div>
      </section>
    </>
  );
}
